import {
  Children,
  cloneElement,
  CSSProperties,
  EffectCallback,
  ReactNode,
  isValidElement,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { isLatLngLiteral } from '@googlemaps/typescript-guards';
import { createCustomEqual } from 'fast-equals';
import styles from './Map.module.less';
import { FormInstance } from 'antd';

export const Map = ({
  children,
  style,
  onClick,
  onIdle,
  onFindByAddress,
  onGetCurrentPosition,
  form,
  ...options
}: google.maps.MapOptions & {
  style: CSSProperties;
  onFindByAddress?: (
    map: google.maps.Map,
    infoWindow: google.maps.InfoWindow,
    geocoder: google.maps.Geocoder,
  ) => Promise<void>;
  onGetCurrentPosition?: (
    map: google.maps.Map,
    infoWindow: google.maps.InfoWindow,
    geocoder: google.maps.Geocoder,
  ) => void;
  onClick?: (
    e: google.maps.MapMouseEvent,
    map: google.maps.Map,
    infoWindow: google.maps.InfoWindow,
    placesService: google.maps.places.PlacesService,
    geocoder: google.maps.Geocoder,
  ) => void;
  onIdle?: (map: google.maps.Map) => void;
  form?: FormInstance;
  children: ReactNode;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();

  const infoWindow = useMemo(
    () =>
      new google.maps.InfoWindow({
        pixelOffset: new google.maps.Size(1, -13),
      }),
    [],
  );
  const geocoder = useMemo(() => new google.maps.Geocoder(), []);
  const placesService = useMemo(
    () =>
      map && google.maps.places && new google.maps.places.PlacesService(map),
    [map],
  );

  useEffect(() => {
    if (ref.current && !map) {
      const map = new window.google.maps.Map(ref.current, {
        mapTypeControl: false,
        streetViewControl: false,
      });

      if (onGetCurrentPosition) {
        const locationButton = document.createElement('button');
        locationButton.textContent = 'Đến vị trí hiện tại';
        locationButton.classList.add(styles.locationButton);
        locationButton.addEventListener('click', (e) => {
          // e.stopPropagation();
          e.preventDefault();
          onGetCurrentPosition(map, infoWindow, geocoder);
        });
        map.controls[google.maps.ControlPosition.TOP_LEFT].push(locationButton);
      }

      if (onFindByAddress) {
        const findByAddressButton = document.createElement('button');
        findByAddressButton.textContent = 'Tìm theo địa chỉ';
        findByAddressButton.classList.add(styles.locationButton);
        findByAddressButton.addEventListener('click', (e) => {
          // e.stopPropagation();
          e.preventDefault();
          const addressToSearch = form?.getFieldValue('addressToSearch');
          if (!addressToSearch) return;
          onFindByAddress(map, infoWindow, geocoder);
        });
        map.controls[google.maps.ControlPosition.TOP_LEFT].push(
          findByAddressButton,
        );
      }

      setMap(map);
    }
  }, [
    ref,
    map,
    infoWindow,
    geocoder,
    onGetCurrentPosition,
    onFindByAddress,
    form,
  ]);

  useDeepCompareEffectForMaps(() => {
    if (map) {
      map.setOptions(options);
    }
  }, [map, options]);

  useEffect(() => {
    if (map) {
      ['click', 'idle'].forEach((eventName) =>
        google.maps.event.clearListeners(map, eventName),
      );

      if (onClick) {
        map.addListener('click', (e: any) => {
          if (!placesService) return;
          onClick(e, map, infoWindow, placesService, geocoder);
        });
      }

      if (onIdle) {
        map.addListener('idle', () => onIdle(map));
      }
    }
  }, [map, onClick, onIdle, placesService, geocoder, infoWindow]);

  return (
    <>
      <div ref={ref} style={style} />
      {Children.map(children, (child) => {
        if (isValidElement(child)) {
          // set the map prop on the child component
          return cloneElement(child, {map} as any);
        }
      })}
    </>
  );
};

const deepCompareEqualsForMaps = createCustomEqual(
  (deepEqual) => (a: any, b: any) => {
    if (
      isLatLngLiteral(a) ||
      a instanceof google.maps.LatLng ||
      isLatLngLiteral(b) ||
      b instanceof google.maps.LatLng
    ) {
      return new google.maps.LatLng(a).equals(new google.maps.LatLng(b));
    }

    // TODO extend to other types

    // use fast-equals for other objects
    return deepEqual(a, b);
  },
);

function useDeepCompareMemoize(value: any) {
  const ref = useRef();

  if (!deepCompareEqualsForMaps(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
}
function useDeepCompareEffectForMaps(
  callback: EffectCallback,
  dependencies: any[],
) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(callback, dependencies.map(useDeepCompareMemoize));
}
